<template>
  <div
    class="code-input__container"
    @click="handleFocus"
  >
    <input
      v-for="(_, index) in Array.from({ length })"
      :key="index"
      class="code-input"
      :class="[
        {
          'code-input--error': error
        },
        inputClass
      ]"
      ref="inputs"
      v-model="codes[index]"
      type="tel"
      maxlength="1"
      autocomplete="off"
      :placeholder="placeholder"
      @input="onInput(index, $event)"
      @keydown.delete="onDelete(index, $event)"
      @paste="onPaste"
      @focus="onFocus"
      @blur="onBlur"
    >
  </div>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'

const props = defineProps({
  length: {
    type: Number,
    default: 6,
    validator: (value) => value > 0
  },
  modelValue: {
    type: String,
    default: ''
  },
  inputClass: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: '-'
  },
  error: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'input', 'blur'])

const inputs = ref([])
const codes = ref(Array(props.length).fill(''))

// 监听外部值变化
watch(() => props.modelValue, (newVal) => {
  codes.value = (newVal ?? '').split('')
}, {
  immediate: true
})

// 处理输入
const onInput = (index, event) => {
  const value = event.target.value.replace(/\D/g, '')
  codes.value[index] = value

  emit('input', event)
  updateModelValue()

  if (value === '' || value == null) return

  if (index < props.length - 1) {
    nextTick(() => {
      inputs.value[index + 1]?.focus()
    })
  } else {
    inputs.value[index].blur()
  }
}

// 处理删除
const onDelete = (index) => {
  if (!codes.value[index] && index > 0) {
    nextTick(() => {
      inputs.value[index - 1]?.focus()
    })
  }
}

/** 判断 CodeInput 是否真的失焦，而非跳转到下一个 input */
let isFocus = false

const onFocus = () => {
  isFocus = true
}

const onBlur = (event) => {
  isFocus = false
  nextTick(() => {
    if (!isFocus) emit('blur', event)
  })
}
/** end */

// 处理粘贴
const onPaste = (event) => {
  event.preventDefault()
  const pasteData = event.clipboardData
    .getData('text')
    .replace(/\D/g, '')
    .slice(0, props.length)
    .split('')

  pasteData.forEach((char, i) => {
    if (i < props.length) {
      codes.value[i] = char
    }
  })

  nextTick(() => {
    const lastFilledIndex = pasteData.length - 1
    if (lastFilledIndex < props.length - 1) {
      inputs.value[lastFilledIndex + 1]?.focus()
    }
    updateModelValue()
  })
}

const handleFocus = (e) => {
  inputs.value[Math.min(props.modelValue.length, props.length - 1)].focus()
}

// 更新绑定值
const updateModelValue = () => {
  const codeString = codes.value.join('')
  emit('update:modelValue', codeString)
}

</script>

<style lang="less">
.code-input {
  &__container {
    display: flex;
    justify-content: space-between;
    border-radius: 4px;
  }

  width: 40px;
  height: 40px;
  text-align: center;
  caret-color: #1153D6;
  pointer-events: none;
  border: 1px solid #EBF1F5;
  border-radius: 4px;
  transition: all .2s;
  font-family: mon-500;
  font-size: 12px;
  color: #232323;

  &::placeholder {
    color: #B2B7C2;
  }

  &:focus {
    outline: none;
    border-width: 1.5px;
    border-color: #232323;
  }

  &--error {
    border-color: #F54550;

    &:focus {
      border-width: 1.5px;
      border-color: #F53131;
    }
  }
}
</style>
